import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import CustomButton from "../components/CustomButton";

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmColor?: 'error' | 'primary' | 'secondary' | 'success' | 'warning' | 'info';
}

interface ConfirmContextValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

interface ConfirmState {
  open: boolean;
  options: ConfirmOptions | null;
  resolve: ((value: boolean) => void) | null;
}

const ConfirmContext = createContext<ConfirmContextValue | undefined>(undefined);

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const [state, setState] = useState<ConfirmState>({
    open: false,
    options: null,
    resolve: null,
  });

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setState({
        open: true,
        options,
        resolve,
      });
    });
  }, []);

  const handleClose = useCallback((result: boolean) => {
    state.resolve?.(result);
    setState({
      open: false,
      options: null,
      resolve: null,
    });
  }, [state.resolve]);

  const value = useMemo<ConfirmContextValue>(() => ({ confirm }), [confirm]);

  const confirmColor = state.options?.confirmColor ?? 'error';

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <Dialog
        open={state.open}
        onClose={() => handleClose(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            px: 1,
            pb: 1,
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pt: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 44,
              height: 44,
              borderRadius: '50%',
              backgroundColor: (theme) => alpha(theme.palette[confirmColor].main, 0.12),
              color: (theme) => theme.palette[confirmColor].main,
            }}
          >
            <WarningAmberIcon />
          </Box>
          <Typography variant="h6" fontWeight={700} sx={{ color: 'text.primary' }}>
            {state.options?.title ?? t('common.confirmTitle', 'Confirm')}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <DialogContentText sx={{ color: 'text.secondary', fontSize: '0.95rem' }}>
            {state.options?.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <CustomButton
            onClick={() => handleClose(false)}
            variant="secondary"
          >
            {state.options?.cancelLabel ?? t('common.cancel', 'Cancel')}
          </CustomButton>
          <CustomButton
            onClick={() => handleClose(true)}
            variant="primary"
          >
            {state.options?.confirmLabel ?? t('common.delete', 'Delete')}
          </CustomButton>
        </DialogActions>
      </Dialog>
    </ConfirmContext.Provider>
  );
}


export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used within ConfirmProvider');
  }
  return ctx.confirm;
}
